import type { Event } from '@/lib/events';
import type { RouteLibraryItem } from '@/lib/routes';

export type LandingRunMetric = Readonly<{
  label: string;
  unit?: string;
  value: string;
}>;

export type LandingRunPresentation = Readonly<{
  description: string;
  metrics: readonly LandingRunMetric[];
  title: string;
}>;

export const FALLBACK_LANDING_RUN: LandingRunPresentation = {
  description:
    'Monday at Lumphini Park Gate #1. Weekly 3.5 KM city route, social pace. Arrive early for check-in.',
  metrics: [
    { label: 'Distance', value: '3.5', unit: 'km' },
    { label: 'Meet', value: '06:00', unit: 'am' },
    { label: 'Type', value: 'Weekly', unit: 'run' },
    { label: 'Location', value: 'Gate #1' },
  ],
  title: 'Weekly Monday 6am Run',
};

function isMondayRun(event: Event, timeZone: string): boolean {
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'long',
  }).format(new Date(event.starts_at));
  return weekday === 'Monday';
}

/**
 * The public landing is the club's Monday-run poster. Prefer the next Monday
 * event with an attached route, while retaining useful fallbacks for clubs
 * that have not attached a route yet.
 */
export function selectLandingEvent(
  rankedEvents: readonly Event[],
  timeZone = 'Asia/Bangkok'
): Event | null {
  return (
    rankedEvents.find((event) => Boolean(event.route_version_id) && isMondayRun(event, timeZone)) ??
    rankedEvents.find((event) => isMondayRun(event, timeZone)) ??
    rankedEvents.find((event) => Boolean(event.route_version_id)) ??
    rankedEvents[0] ??
    null
  );
}

function distanceMetric(route: RouteLibraryItem | null): LandingRunMetric {
  if (!route) return { label: 'Distance', value: 'TBA' };
  const kilometers = route.version.distance_m / 1000;
  return {
    label: 'Distance',
    unit: 'km',
    value: kilometers < 10 ? kilometers.toFixed(1) : String(Math.round(kilometers)),
  };
}

function meetMetric(startsAt: string, timeZone: string): LandingRunMetric {
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    hour12: true,
    minute: '2-digit',
    timeZone,
  }).formatToParts(new Date(startsAt));
  const hour = parts.find((part) => part.type === 'hour')?.value ?? '--';
  const minute = parts.find((part) => part.type === 'minute')?.value ?? '--';
  const period = parts.find((part) => part.type === 'dayPeriod')?.value.toLowerCase();
  return { label: 'Meet', value: `${hour}:${minute}`, unit: period };
}

function runTypeMetric(event: Event, timeZone: string): LandingRunMetric {
  if (event.title.toLowerCase().includes('weekly')) {
    return { label: 'Type', value: 'Weekly', unit: 'run' };
  }
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'long',
  }).format(new Date(event.starts_at));
  return { label: 'Type', value: weekday, unit: 'run' };
}

export function landingRunPresentation(
  event: Event | null,
  route: RouteLibraryItem | null,
  timeZone = 'Asia/Bangkok'
): LandingRunPresentation {
  if (!event) return FALLBACK_LANDING_RUN;
  return {
    description:
      event.description?.trim() ||
      `Meet at ${event.place_name}. Open the event for the latest run details.`,
    metrics: [
      distanceMetric(route),
      meetMetric(event.starts_at, timeZone),
      runTypeMetric(event, timeZone),
      { label: 'Location', value: event.place_name },
    ],
    title: event.title,
  };
}
